import { showKomboConnect } from '@kombo-api/connect'
import { useState } from 'react'
import { toast } from 'sonner'

import { usePostPayrollKomboCreateReconnectLink } from 'utils/web_api'

import { type KomboActivationResponse, useActivateKomboIntegration } from './useActivateKomboIntegration'
import { useCreateKomboConnectLink } from './useCreateKomboConnectLink'

type ProviderCapabilities = {
  supportsAutomatedDeductions: boolean;
  supportsPaySchedules: boolean;
}
type OnSuccessCallback = (providerCapabilities: ProviderCapabilities) => void

interface UseKomboIntegrationProps {
  onSuccessCallback: OnSuccessCallback;
}

// Define the response type to match useCreateKomboConnectLink
type KomboConnectLink = {
  link: string;
}

export function useKomboIntegration ({ onSuccessCallback }: UseKomboIntegrationProps) {
  const [komboLink, setKomboLink] = useState<string | undefined>(undefined)
  const [reconnectLink, setReconnectLink] = useState<string | undefined>(undefined)
  const [providerIdToOpen, setProviderIdToOpen] = useState<string | undefined>(undefined)

  const createLinkMutation = useCreateKomboConnectLink()
  const createReconnectLinkMutation = usePostPayrollKomboCreateReconnectLink({
    request: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  })
  const activateIntegrationMutation = useActivateKomboIntegration()

  const startConnection = async (providerId: string) => {
    setProviderIdToOpen(providerId)

    return new Promise<void>((resolve) => {
      createLinkMutation.mutate({ payrollProviderConfigId: providerId }, {
        onSuccess: ({ link }: KomboConnectLink) => {
          setKomboLink(link)
          resolve()
        },
        onError: () => {
          toast.error('Error connecting to payroll provider, please try again.')
          resolve()
        }
      })
    })
  }

  const startReauthentication = async () => {
    return new Promise<void>((resolve) => {
      createReconnectLinkMutation.mutate(undefined, {
        onSuccess: (response: any) => {
          setReconnectLink(response.link)
          resolve()
        },
        onError: () => {
          toast.error('Error starting reauthentication, please try again.')
          resolve()
        }
      })
    })
  }

  const openModal = async () => {
    const linkToUse = reconnectLink || komboLink
    if (linkToUse) {
      try {
        // Show Kombo Connect and get the activation token
        const activationToken = await showKomboConnect(linkToUse)

        // Activate the integration with the token
        activateIntegrationMutation.mutate({ token: activationToken }, {
          onSuccess: (data: KomboActivationResponse) => {
            if (data.connection_complete) {
              onSuccessCallback({
                supportsAutomatedDeductions: false,
                supportsPaySchedules: false
              })
            }
          },
          onError: () => {
            toast.error('Error activating payroll integration, please try again or contact support.')
          }
        })
      } catch {
        toast.error('Error connecting to payroll provider, please try again.')
      } finally {
        setProviderIdToOpen(undefined)
        setReconnectLink(undefined)
      }
    }
  }

  return {
    startConnection,
    startReauthentication,
    openModal,
    komboLink,
    reconnectLink,
    providerIdToOpen,
    setProviderIdToOpen
  }
}
